import { Observable, of, ReplaySubject } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import DIContainer from '@components/DependencyInjectionContainer.js';
export class CacheStorageAdapter_IndexedDB {
    constructor(dbName) {
        this.db = null;
        this.requiredStores = new Set();
        this.storeSchema = new Map();
        this._isReadySubject = new ReplaySubject(1);
        this.isReady$ = this._isReadySubject.asObservable();
        this.dbName = dbName;
        this.dbVersion = 108;
        // IMPORTANT: Any change to the requiredStores (adding/renaming/removing) should increment the dbVersion property by +1.
        if (dbName === 'q360mobile') {
            this.requiredStores.add('tasklist');
            this.requiredStores.add('offlinedata');
            this.requiredStores.add('outbox');
            this.requiredStores.add('outbox_baselines');
            this.requiredStores.add('client');
            this.requiredStores.add('request');
            this.requiredStores.add('persistent');
            this.requiredStores.add('application');
            this.requiredStores.add('session');
        }
        else {
            this.requiredStores.add('toolbar');
            this.requiredStores.add('client');
            this.requiredStores.add('request');
            this.requiredStores.add('timebill');
            this.requiredStores.add('clipboard');
            this.requiredStores.add('session');
            this.requiredStores.add('persistent');
            this.requiredStores.add('application');
        }
        const schema = this.getSchema();
        schema.forEach((d) => this.storeSchema.set(d.name, d));
        this.listenForExternalTabVersionUpdates();
        this.initializeDatabase().subscribe({
            next: () => this._isReadySubject.next(true),
            error: (err) => this._isReadySubject.error(err)
        });
    }
    getStoreNames() {
        return Array.from(this.storeSchema.keys());
    }
    getStoreKeyPath(storeName) {
        return this.storeSchema.get(storeName)?.keyPath;
    }
    getStoreIndexes(storeName) {
        return this.storeSchema.get(storeName)?.indexes ?? [];
    }
    deleteAllStores() {
        return this.openDB().pipe(switchMap(() => new Observable((observer) => {
            if (!this.db) {
                observer.error(new Error('Database is not initialized.'));
                return;
            }
            const transaction = this.db.transaction(Array.from(this.db.objectStoreNames), 'readwrite');
            const objectStoreNames = Array.from(this.db.objectStoreNames);
            let completedStores = 0;
            objectStoreNames.forEach((storeName) => {
                const objectStore = transaction.objectStore(storeName);
                const clearRequest = objectStore.clear();
                clearRequest.onsuccess = () => {
                    completedStores++;
                    if (completedStores === objectStoreNames.length) {
                        observer.next();
                        observer.complete();
                    }
                };
                clearRequest.onerror = (event) => {
                    observer.error(event.target.error);
                };
            });
            transaction.onerror = (event) => {
                observer.error(event.target.error);
            };
        })));
    }
    setCache(cacheName, key, value) {
        const keyPath = this.getStoreKeyPath(cacheName);
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => new Observable((observer) => {
            let request;
            if (keyPath) {
                this.assertKeyPathMatches(keyPath, key, value);
                request = objectStore.put(value);
            }
            else {
                request = objectStore.put(value, key);
            }
            request.onsuccess = () => {
                observer.next();
                observer.complete();
            };
            request.onerror = (event) => {
                observer.error(event.target.error);
            };
        })));
    }
    setCacheBatch(cacheName, keyValuePairs) {
        const keyPath = this.getStoreKeyPath(cacheName);
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => new Observable((observer) => {
            let errorOccurred = false;
            for (const key in keyValuePairs) {
                if (!Reflect.has(keyValuePairs, key))
                    continue;
                if (errorOccurred)
                    break;
                const value = keyValuePairs[key];
                let request;
                try {
                    if (keyPath) {
                        this.assertKeyPathMatches(keyPath, key, value);
                        request = objectStore.put(value);
                    }
                    else {
                        request = objectStore.put(value, key);
                    }
                }
                catch (e) {
                    errorOccurred = true;
                    observer.error(e);
                    break;
                }
                request.onerror = (event) => {
                    if (!errorOccurred) {
                        errorOccurred = true;
                        observer.error(event.target.error);
                    }
                };
            }
            objectStore.transaction.oncomplete = () => {
                if (errorOccurred)
                    return;
                observer.next();
                observer.complete();
            };
            const txFail = (event) => {
                if (!errorOccurred) {
                    errorOccurred = true;
                    observer.error(event?.target?.error ?? new Error('IndexedDB transaction failed.'));
                }
            };
            objectStore.transaction.onerror = txFail;
            objectStore.transaction.onabort = txFail;
        })));
    }
    getCache(cacheName, key, options = {}) {
        return this.getObjectStore(cacheName, 'readonly').pipe(switchMap((objectStore) => new Observable((observer) => {
            const request = objectStore.get(key);
            request.onsuccess = () => {
                if (typeof request.result === 'undefined') {
                    if (Reflect.has(options, 'defaultReturnValue')) {
                        observer.next(options.defaultReturnValue);
                        observer.complete();
                        return;
                    }
                    observer.error('requested cache key does not exist');
                }
                else {
                    observer.next(request.result);
                    observer.complete();
                }
            };
            request.onerror = (event) => {
                if (Reflect.has(options, 'defaultReturnValue')) {
                    observer.next(options.defaultReturnValue);
                    observer.complete();
                    return;
                }
                observer.error(event.target.error);
            };
        })));
    }
    deleteCache(cacheName, key) {
        return this.openDB().pipe(switchMap(() => new Observable((observer) => {
            this.getObjectStore(cacheName).subscribe({
                next: (objectStore) => {
                    if (key) {
                        const delReq = objectStore.delete(key);
                        delReq.onsuccess = () => {
                            observer.next();
                            observer.complete();
                        };
                        delReq.onerror = (event) => {
                            observer.error(event.target.error);
                        };
                        return;
                    }
                    const clearRequest = objectStore.clear();
                    clearRequest.onsuccess = () => {
                        observer.next();
                        observer.complete();
                    };
                    clearRequest.onerror = (event) => {
                        observer.error(event.target.error);
                    };
                },
                error: (err) => observer.error(err),
            });
        })));
    }
    deleteCacheByPrefix(cacheName, prefix) {
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => new Observable((observer) => {
            const deletedEntries = [];
            const upperBound = prefix + '\uFFFF';
            const range = IDBKeyRange.bound(prefix, upperBound, false, true);
            const cursorReq = objectStore.openCursor(range);
            cursorReq.onsuccess = (e) => {
                const cursor = e.target.result;
                if (!cursor) {
                    observer.next(deletedEntries);
                    observer.complete();
                    return;
                }
                const key = cursor.key;
                const value = cursor.value;
                if (typeof key === 'string' && key.startsWith(prefix)) {
                    const delReq = cursor.delete();
                    delReq.onsuccess = () => {
                        deletedEntries.push({ key, value });
                        cursor.continue();
                    };
                    delReq.onerror = (ev) => observer.error(ev.target.error);
                }
                else {
                    cursor.continue();
                }
            };
            cursorReq.onerror = (ev) => observer.error(ev.target.error);
        })));
    }
    deleteCacheByPredicate(cacheName, predicate) {
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => new Observable((observer) => {
            const deletedEntries = [];
            const cursorReq = objectStore.openCursor();
            const processCursor = /*async*/ (cursor) => {
                if (!cursor) {
                    observer.next(deletedEntries);
                    observer.complete();
                    return;
                }
                const key = cursor.key;
                const value = cursor.value;
                if (typeof key === 'string') {
                    try {
                        const shouldDelete = /*await*/ predicate({
                            key,
                            value
                        });
                        if (shouldDelete) {
                            const delReq = cursor.delete();
                            delReq.onsuccess = () => {
                                deletedEntries.push({ key, value });
                                cursor.continue();
                            };
                            delReq.onerror = (e) => observer.error(e.target.error);
                            return;
                        }
                    }
                    catch (err) {
                        observer.error(err);
                        return;
                    }
                }
                cursor.continue();
            };
            cursorReq.onsuccess = (e) => {
                const cursor = e.target
                    .result;
                processCursor(cursor);
            };
            cursorReq.onerror = (e) => observer.error(e.target.error);
        })));
    }
    getAll(cacheName) {
        return this.getObjectStore(cacheName, 'readonly').pipe(switchMap((store) => new Observable((observer) => {
            const req = store.getAll();
            req.onsuccess = () => { observer.next(req.result || []); observer.complete(); };
            req.onerror = (e) => observer.error(e.target.error);
        })));
    }
    getAllByIndex(cacheName, indexName, query) {
        return this.getObjectStore(cacheName, 'readonly').pipe(switchMap((store) => new Observable((observer) => {
            const idx = store.index(indexName);
            const req = idx.getAll(query);
            req.onsuccess = () => { observer.next(req.result || []); observer.complete(); };
            req.onerror = (e) => observer.error(e.target.error);
        })));
    }
    ensureReady(operation) {
        return this.isReady$.pipe(filter((ready) => ready), switchMap(() => operation), take(1));
    }
    initializeDatabase() {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        broadcastManager.send(`db_version_initializing_${this.dbName}`, { payload: { newVersion: this.dbVersion } });
        return new Observable((observer) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);
            request.onupgradeneeded = (event) => {
                this.db = event.target.result;
                const tx = event.target.transaction;
                console.log(`Upgrading DB from version ${this.db.version}`);
                this.createMissingStoresAndIndexes(tx);
            };
            request.onsuccess = (event) => {
                this.db = event.target.result;
                console.log('IndexedDB initialized successfully.');
                broadcastManager.send('db_version_initialized', { payload: { newVersion: this.db.version } });
                observer.next();
                observer.complete();
                this.db.onversionchange = () => {
                    console.warn(`[${this.dbName}] versionchange -> closing DB connection`);
                    this.db?.close();
                    this.db = null;
                    this._isReadySubject.next(false);
                };
            };
            request.onerror = (event) => {
                console.log('IndexedDB error', event);
                observer.error(new Error('Database error: ' + event.target.error));
            };
            request.onblocked = () => {
                console.warn('Database upgrade blocked. Please close other connections (tabs) and/or refresh this tab.');
                alert('Database upgrade blocked. Please close other tabs and refresh.');
            };
        });
    }
    openDB(force) {
        const openDb$ = new Observable((observer) => {
            if (this.db) {
                observer.next();
                observer.complete();
                return;
            }
            const request = indexedDB.open(this.dbName, this.dbVersion);
            request.onsuccess = (event) => {
                this.db = event.target.result;
                observer.next();
                observer.complete();
                this.db.onversionchange = () => {
                    console.warn(`[${this.dbName}] versionchange -> closing DB connection`);
                    this.db?.close();
                    this.db = null;
                    this._isReadySubject.next(false);
                };
            };
            request.onerror = (event) => {
                observer.error(new Error('Database error: ' + event.target.error));
            };
        });
        return force ? openDb$ : this.ensureReady(openDb$);
    }
    createMissingStoresAndIndexes(tx) {
        if (!this.db)
            return;
        const schema = this.getSchema();
        for (const def of schema) {
            if (!this.db.objectStoreNames.contains(def.name)) {
                if (def.keyPath) {
                    this.db.createObjectStore(def.name, { keyPath: def.keyPath, ...(def.options ?? {}) });
                }
                else {
                    this.db.createObjectStore(def.name);
                }
            }
            if (def.indexes?.length) {
                const store = tx.objectStore(def.name);
                for (const idx of def.indexes) {
                    if (!store.indexNames.contains(idx.name)) {
                        store.createIndex(idx.name, idx.keyPath, idx.options ?? { unique: false });
                    }
                }
            }
        }
    }
    getObjectStore(storeName, mode = 'readwrite') {
        return this.openDB().pipe(switchMap(() => {
            if (!this.db) {
                throw new Error('Database is not initialized.');
            }
            try {
                return of(this.db.transaction(storeName, mode).objectStore(storeName));
            }
            catch (e) {
                throw new Error(`Failed to open object store "${storeName}" (mode: ${mode}). Available stores: ${Array.from(this.db.objectStoreNames).join(', ')}. Original error: ${e}`);
            }
        }));
    }
    getSchema() {
        if (this.dbName === 'q360mobile') {
            return [
                { name: 'client' },
                { name: 'request' },
                { name: 'persistent' },
                { name: 'session' },
                { name: 'application' },
                {
                    name: 'tasklist',
                    keyPath: '_id'
                },
                {
                    name: 'offlinedata',
                    keyPath: '_id',
                    indexes: [
                        { name: 'by_parentKey', keyPath: '_parentid' },
                        { name: 'by_tablename', keyPath: '_tablename' },
                        { name: 'by_table_parent', keyPath: ['_tablename', '_parentid'] },
                        { name: 'by_recordtype', keyPath: '_recordtype' },
                    ]
                },
                {
                    name: 'outbox',
                    keyPath: 'id',
                    indexes: [
                        { name: 'by_idempotencyKey', keyPath: 'idempotencyKey', options: { unique: true } },
                        { name: 'by_recordKey', keyPath: 'recordKey' },
                        { name: 'by_createdAt', keyPath: 'createdAt' },
                    ]
                },
                {
                    name: 'outbox_baselines',
                    keyPath: 'recordKey'
                },
            ];
        }
        return [
            { name: 'toolbar' },
            { name: 'client' },
            { name: 'request' },
            { name: 'timebill' },
            { name: 'clipboard' },
            { name: 'session' },
            { name: 'persistent' },
            { name: 'application' },
        ];
    }
    listenForExternalTabVersionUpdates() {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        broadcastManager.listen(`db_version_initializing_${this.dbName}`).pipe(filter((msg) => msg.payload.newVersion > this.dbVersion)).subscribe((msg) => {
            const newVersion = msg.payload.newVersion;
            console.log(`Received db_version_initializing_${this.dbName} for version ${newVersion} - closing local connection for version ${this.dbVersion}.`);
            if (this.db) {
                this.db.close();
                this.db = null;
            }
            this._isReadySubject.next(false);
            this.listenForExternalTabVersionUpdateComplete(newVersion);
        });
    }
    listenForExternalTabVersionUpdateComplete(newVersion) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        broadcastManager.listen('db_version_initialized')
            .pipe(filter((msg) => msg.payload.newVersion === newVersion), take(1))
            .subscribe(() => {
            console.log(`Received db_version_initialized for version ${newVersion} - reinitializing DB at new version.`);
            this.dbVersion = newVersion;
            this.openDB(true).subscribe({
                next: () => {
                    this._isReadySubject.next(true);
                },
                error: (err) => this._isReadySubject.error(err)
            });
        });
    }
    assertKeyPathMatches(keyPath, key, value) {
        if (typeof keyPath !== 'string')
            return;
        const v = value?.[keyPath];
        if (typeof v === 'undefined') {
            value[keyPath] = key;
            return;
        }
        if (String(v) !== String(key)) {
            throw new Error(`IndexedDB key mismatch for store "${keyPath}": passed key="${key}" but value.${keyPath}="${v}"`);
        }
    }
}
